import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Play, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';

type ColumnCounts = {
  candidate: number;
  enriched: number;
  qa_failed: number;
  published: number;
  total: number;
};

type JobRun = {
  id: number;
  job_name: string;
  landscape_key?: string | null;
  status: string;
  started_at?: string | null;
  finished_at?: string | null;
  stats?: Record<string, unknown>;
  error_message?: string;
  candidates_written?: number | null;
  columns_promoted?: number | null;
  enriched?: number | null;
  published_columns?: number | null;
  blocked_columns?: number | null;
  passed?: number | null;
  failed?: number | null;
};

type LandscapeRow = {
  key: string;
  title: string;
  cancer_type?: string | null;
  line_label?: string | null;
  status: string;
  data_cutoff?: string | null;
  column_counts: ColumnCounts;
  last_job?: JobRun | null;
  ready_to_view?: boolean;
};

type PipelineAction = 'scout' | 'enrich' | 'format_qa' | 'source_qa' | 'promote';

const ACTIONS: { id: PipelineAction; label: string; primary?: boolean }[] = [
  { id: 'scout', label: 'Run scout', primary: true },
  { id: 'enrich', label: 'Enrich' },
  { id: 'format_qa', label: 'Format QA' },
  { id: 'source_qa', label: 'Source QA' },
  { id: 'promote', label: 'Promote' },
];

function formatWhen(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatStats(run?: JobRun | null): string {
  if (!run) return '';
  const parts: string[] = [];
  if (run.candidates_written != null) parts.push(`${run.candidates_written} candidates`);
  if (run.columns_promoted != null) parts.push(`${run.columns_promoted} columns promoted`);
  if (run.enriched != null) parts.push(`${run.enriched} enriched`);
  if (run.published_columns != null) parts.push(`${run.published_columns} published`);
  if (run.blocked_columns != null) parts.push(`${run.blocked_columns} blocked`);
  if (run.passed != null || run.failed != null) {
    parts.push(`QA ${run.passed ?? 0} pass / ${run.failed ?? 0} fail`);
  }
  return parts.join(' · ');
}

const LandscapePipelinePanel: React.FC = () => {
  const [landscapes, setLandscapes] = useState<LandscapeRow[]>([]);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [meta, setMeta] = useState<{ project?: string; region?: string }>({});

  const load = useCallback(async () => {
    const [listRes, runsRes] = await Promise.all([
      apiService.fetchLandscapePipelineLandscapes(),
      apiService.fetchLandscapePipelineRuns(12),
    ]);
    if (listRes?.success) {
      setLandscapes(listRes.landscapes || []);
      setPipelineRunning(Boolean(listRes.running));
      setMeta({ project: listRes.project, region: listRes.region });
      setSelected((prev) => {
        if (prev.size > 0) return prev;
        return new Set((listRes.landscapes || []).map((l: LandscapeRow) => l.key));
      });
    } else if (listRes?.error) {
      setError(listRes.error);
    }
    if (runsRes?.success) {
      setRuns(runsRes.runs || []);
      if (runsRes.running != null) setPipelineRunning(Boolean(runsRes.running));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await load();
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load landscape pipeline');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (!pipelineRunning && busyAction == null) return undefined;
    const id = window.setInterval(() => {
      load().catch(() => {
        /* keep polling */
      });
    }, 8000);
    return () => window.clearInterval(id);
  }, [pipelineRunning, busyAction, load]);

  const selectedKeys = useMemo(() => Array.from(selected), [selected]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(landscapes.map((l) => l.key)));
  const selectNone = () => setSelected(new Set());

  const runAction = async (action: PipelineAction) => {
    if (selectedKeys.length === 0) {
      setError('Select at least one landscape (HCC / DLBCL).');
      return;
    }
    setError(null);
    setMessage(null);
    setBusyAction(action);
    try {
      const res = await apiService.runLandscapePipeline({
        action,
        landscape_keys: selectedKeys,
      });
      if (!res?.success) {
        throw new Error(res?.error || res?.detail || 'Failed to start job');
      }
      setMessage(res.message || `Started ${action}`);
      setPipelineRunning(true);
      await load();
    } catch (err: unknown) {
      const messageText =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string; detail?: string } } }).response?.data
              ?.error ||
            (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to start job';
      setError(messageText || 'Failed to start job');
    } finally {
      setBusyAction(null);
    }
  };

  const statusBadge = pipelineRunning
    ? {
        label: 'Job in progress',
        className: 'bg-amber-50 text-amber-900 border-amber-200',
        icon: <Loader2 size={14} className="animate-spin" />,
      }
    : landscapes.some((l) => l.ready_to_view)
      ? {
          label: 'Ready to view',
          className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
          icon: <CheckCircle2 size={14} />,
        }
      : {
          label: 'Idle',
          className: 'bg-slate-50 text-slate-600 border-slate-200',
          icon: null,
        };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
            Landscape pipeline
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Cloud Run Jobs for the <span className="font-semibold">emerging_landscape</span> DB
            (HCC / DLBCL only). Writes candidates / columns — not the legacy Emerging scrape or
            ruby-data CSV uploads below.
          </p>
          {(meta.project || meta.region) && (
            <p className="text-[11px] text-slate-400 mt-1 font-mono">
              {meta.project}/{meta.region}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load()}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            <RefreshCw size={12} />
            Refresh
          </button>
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${statusBadge.className}`}
          >
            {statusBadge.icon}
            {statusBadge.label}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 size={20} className="animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button type="button" onClick={selectAll} className="font-semibold text-indigo-600">
              Select all
            </button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={selectNone} className="font-semibold text-slate-500">
              Clear
            </button>
          </div>

          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
            {landscapes.map((row) => {
              const checked = selected.has(row.key);
              const counts = row.column_counts || {
                candidate: 0,
                enriched: 0,
                qa_failed: 0,
                published: 0,
                total: 0,
              };
              return (
                <li
                  key={row.key}
                  className={`px-3.5 py-3 flex flex-wrap items-start gap-3 ${
                    checked ? 'bg-indigo-50/40' : 'bg-white'
                  }`}
                >
                  <label className="flex items-start gap-2.5 min-w-[220px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(row.key)}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">{row.title}</span>
                      <span className="block text-[11px] text-slate-500 font-mono">{row.key}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {row.cancer_type} · {row.line_label}
                      </span>
                    </span>
                  </label>
                  <div className="flex-1 min-w-[160px] text-xs text-slate-600 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          row.status === 'published'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {row.status}
                      </span>
                      {row.ready_to_view && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          <CheckCircle2 size={12} />
                          Ready to view
                        </span>
                      )}
                    </div>
                    <div>
                      columns: {counts.published} published · {counts.enriched} enriched ·{' '}
                      {counts.candidate} candidate · {counts.qa_failed} QA failed
                      {counts.total ? ` (${counts.total} total)` : ''}
                    </div>
                    {row.last_job && (
                      <div className="text-slate-500">
                        last {row.last_job.job_name} · {row.last_job.status} ·{' '}
                        {formatWhen(row.last_job.finished_at || row.last_job.started_at)}
                        {formatStats(row.last_job) ? ` · ${formatStats(row.last_job)}` : ''}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap gap-2 items-center">
            {ACTIONS.map((action) => {
              const busy = busyAction === action.id;
              return (
                <button
                  key={action.id}
                  type="button"
                  disabled={Boolean(busyAction) || selectedKeys.length === 0}
                  onClick={() => runAction(action.id)}
                  className={
                    action.primary
                      ? 'inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60'
                      : 'inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-indigo-300 disabled:opacity-60'
                  }
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : action.primary ? <Play size={14} /> : null}
                  {busy ? 'Starting…' : action.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-none" />
              {error}
            </div>
          )}
          {message && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              {message}
            </div>
          )}

          <div className="border-t border-slate-100 pt-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">
              Recent job runs
            </div>
            {runs.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No landscape job runs yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {runs.map((run) => (
                  <li
                    key={run.id}
                    className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <div>
                      <span className="font-medium text-slate-800">{run.job_name}</span>
                      <span className="text-xs text-slate-500 ml-2 font-mono">
                        {run.landscape_key || '—'}
                      </span>
                      <span className="text-xs text-slate-500 ml-2">
                        {formatWhen(run.started_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {formatStats(run) && <span>{formatStats(run)}</span>}
                      {run.status === 'running' ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                          <Loader2 size={12} className="animate-spin" />
                          running
                        </span>
                      ) : run.status === 'failed' ? (
                        <span className="font-semibold text-rose-600">failed</span>
                      ) : (
                        <span className="font-semibold text-emerald-700">OK</span>
                      )}
                    </div>
                    {run.error_message ? (
                      <div className="w-full text-xs text-rose-600">{run.error_message}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LandscapePipelinePanel;
